import { useCallback, useEffect, useState } from "react";
import styles from "./RHFRenderSelectedCollections.module.css";
import { Button, Thumbnail } from "@shopify/polaris";
import CloseIcon from "../Icons/CloseIcon";
import { CollectionIcon } from "@shopify/polaris-icons";
import { InlineError } from "@shopify/polaris";
import { useFormContext, useWatch } from "react-hook-form";
import { AppConfigContext } from "../../context/AppConfigContext";
import { useContext } from "react";
import { useVariantFieldDisabled } from "../FormFields";
import { getFieldError } from "../../utilities/functions";

const RHFRenderSelectedCollections = ({
    updateKey = "collections",
    multiple = true,
    value,
    onChange,
    errorMessage,
}: {
    updateKey?: string;
    multiple?: boolean;
    /** Override: when provided (with `onChange`), reads from this instead of `updateKey`'s RHF field */
    value?: any[];
    /** Override: when provided, writes go here instead of `setValue(updateKey, ...)` — for callers that
     * manage the array as part of a larger whole-array write (e.g. useFieldArray's `replace()`). */
    onChange?: (value: any[]) => void;
    /** Explicit error to show, for callers using the `value`/`onChange` override (no RHF field to read from) */
    errorMessage?: string;
}) => {
    const {
        control,
        setValue: setFormValue,
        formState: { errors: formErrors },
    } = useFormContext();
    const watchedCollections = useWatch({
        control,
        name: updateKey,
        defaultValue: [],
    });
    const collections = value !== undefined ? value : watchedCollections;
    const [renderCollectionsArr, setRenderCollectionsArr] = useState(collections);
    useContext(AppConfigContext);
    const isAbDisabled = useVariantFieldDisabled(updateKey);
    const fieldError = errorMessage ?? (value === undefined ? getFieldError(formErrors, updateKey) : undefined);

    const setValue = useCallback(
        (key: string, next: any[], options?: any) => {
            if (onChange) onChange(next);
            else setFormValue(key, next, options);
        },
        [onChange, setFormValue],
    );

    useEffect(() => {
        setRenderCollectionsArr(collections);
    }, [collections]);

    const handleCollectionSearch = async (initialQuery: string = "") => {
        if (isAbDisabled) return;
        const preSelected = collections.map((collection: any) => ({
            id: collection.id,
        }));
        const selected = await shopify.resourcePicker({
            type: "collection",
            multiple: multiple ? 10 : false,
            query: initialQuery,
            action: "select",
            selectionIds: preSelected,
        });

        if (selected) {
            const collectionsArr = selected.map((coll: any) => ({
                id: coll?.id,
                handle: coll?.handle,
                image: coll.image,
                title: coll.title,
            }));

            setValue(updateKey, collectionsArr, { shouldDirty: true });
        }
    };

    const removeCollection = (pId: string = "") => {
        if (isAbDisabled) return;
        const temp = [...renderCollectionsArr];
        const idx = temp.findIndex((p: any) => p.id === pId);

        if (idx >= 0) {
            temp.splice(idx, 1);
            setValue(updateKey, temp, { shouldDirty: true });
        }
    };

    return (
        <div>
            <div
                className={styles.comboTitleWrapper}
                style={{ ...(isAbDisabled && { pointerEvents: "none", opacity: "0.5" }) }}
            >
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "10px",
                        alignItems: "center",
                        marginBottom: "10px",
                    }}
                >
                    {renderCollectionsArr?.length > 0 && multiple && (
                        <div>
                            <Button
                                variant="secondary"
                                onClick={() => handleCollectionSearch()}
                                disabled={isAbDisabled}
                            >
                                + Add Collections
                            </Button>
                        </div>
                    )}
                </div>

                {renderCollectionsArr?.length === 0 && (
                    <div
                        className={styles.emptyCollectionBox}
                        onClick={() => handleCollectionSearch()}
                        style={{ ...(isAbDisabled && { pointerEvents: "none", opacity: "0.5" }) }}
                    >
                        <Button variant="secondary" disabled={isAbDisabled}>
                            Add Collection{multiple ? "s" : ""}
                        </Button>
                    </div>
                )}

                {renderCollectionsArr.length > 0 && (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            border: "1px solid rgba(227,227,227,1)",
                            borderRadius: "12px",
                            margin: "6px 0",
                            maxHeight: "260px",
                            overflowY: "auto",
                        }}
                    >
                        {renderCollectionsArr?.map((coll: any) => (
                            <div key={coll.id}>
                                <div className={styles.container}>
                                    <div className={styles.container_wrapper}>
                                        <div className={styles.img_container}>
                                            <Thumbnail
                                                source={coll?.image?.originalSrc || CollectionIcon}
                                                alt={coll?.title}
                                                size="medium"
                                            />
                                        </div>
                                        <p>{coll?.title}</p>
                                    </div>

                                    {!isAbDisabled && (
                                        <div onClick={() => removeCollection(coll.id)} className={styles.closeIcon}>
                                            <CloseIcon />
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {fieldError && (
                <div style={{ marginTop: "6px" }}>
                    <InlineError message={fieldError} fieldID={updateKey} />
                </div>
            )}
        </div>
    );
};

export default RHFRenderSelectedCollections;
